import FileInput from "patient-ping-remedy/packages/file-input";
import Button, {StyleType} from "patient-ping-remedy/packages/button";
import {DownloadWrapper} from "@/components/pages/tools/bulk_user_upload/BulkUserUploadForm.styles";
import {SmallTypography} from "patient-ping-remedy/packages/typography";
import InfoIconTooltip from "@common/InfoIconTooltip";
import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {useMutation, useQuery} from "@tanstack/react-query";
import {ToastType} from "patient-ping-remedy/packages/toast";
import {createFormFieldConfig, useForm} from "@/hooks/useForm";
import Validate from "@/helpers/validate";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {useAlertStore} from "@/store/alert_store";
import FileHelpers from "@/helpers/file_helpers";
import Modal from "patient-ping-remedy/packages/modal";
import Spinner from "patient-ping-remedy/packages/spinner";
import {colors, sizes} from "patient-ping-remedy/packages/theme";
import Icon, {ICONS} from "patient-ping-remedy/packages/icon";
import SortableTable, {TableData} from "@common/SortableTable";
import {css} from "@emotion/css";
import DisplayHelpers from "@/helpers/display_helpers";
import CarecoApi from "@/api/careco-api";
import {JobStatus} from "@/api/dto/client-profile";

const SendSmsTool = () => {
  const {carecoApi} = useContext(CarecoApiContext);
  const {addAlert} = useAlertStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inProgress, setInProgress] = useState<Nullable<boolean>>(null);
  const killPolling = useRef(false);

  const {data: processResponses, isLoading, isFetching, error, refetch} = useQuery({
    queryFn: () => carecoApi?.getSmsResponses(),
    queryKey: ['smsResponses'],
    enabled: !!carecoApi
  });

  const {mutate, isPending} = useMutation({
    mutationFn: async (file: File) =>
      carecoApi?.sendSms(file),
    onSuccess() {
      addAlert({
        type: ToastType.SUCCESS,
        content: 'File is being processed. This may take a few minutes depending on the size of the file. ' +
          'You can leave this page and come back later.',
      });
      resetForm();
      setIsModalOpen(false);
      void pollApi(carecoApi!);
    },
    onError() {
      addAlert({
        type: ToastType.ERROR,
        content: 'Failed to start the SMS process.',
      });
      setInProgress(false);
    },
  });

  const {form, isValid, setInitialForm, handleChange, handleSubmit, resetForm} = useForm(() => {
    setInProgress(true);
    mutate(form.file.value[0]);
  });

  useEffect(() => {
    setInitialForm({
      ...createFormFieldConfig(
        'file',
        [],
        {valid: false, message: ''},
        [Validate.validateFile(undefined, ['text/csv'])],
        true,
      ),
    });
  }, [isModalOpen]);

  useEffect(() => {
    if (!carecoApi) {
      return;
    }
    void pollApi(carecoApi);

    return () => {
      killPolling.current = true;
    };
  }, [carecoApi]);

  const pollApi = async (carecoApi: CarecoApi) => {
    if(inProgress === false) return;

    killPolling.current = false;
    let response = await carecoApi.getSendSmsStatus();

    while (response.status === JobStatus.InProgress && !killPolling.current) {
      setInProgress(true);
      await new Promise(r => setTimeout(r, process.env.REACT_APP_CLIENT_PROFILE_POLL_DELAY));

      response = await carecoApi.getSendSmsStatus();
    }

    setInProgress(false);

    if (response.status === JobStatus.Complete) {
      await refetch();
    }
  };

  const tableData : TableData[] = useMemo(() => {
    return processResponses?.map(attempt => {
      return {
        fileName: {
          tableHeader: 'File name',
          key: 'fileName',
          type: 'string',
          value: attempt.fileName,
          displayValue: attempt.fileName,
          columnWidth: '15%',
        },
        processedDate: {
          tableHeader: 'Processed Date & Time',
          key: 'processedDate',
          type: 'string',
          value: attempt.processedDate,
          displayValue: DisplayHelpers.formatDateTimeShort(attempt.processedDate),
          columnWidth: '25%',
        },
        totalRecords: {
          tableHeader: 'Total records',
          key: 'total',
          type: 'number',
          value: attempt.totalRecords,
          displayValue: attempt.totalRecords,
          columnWidth: '15%',
        },
        totalPhones: {
          tableHeader: 'Total phones',
          key: 'totalPhones',
          type: 'number',
          value: attempt.totalPhones,
          displayValue: attempt.totalPhones,
          columnWidth: '15%',
        },
        success: {
          tableHeader: 'Success',
          key: 'success',
          type: 'number',
          value: attempt.sent,
          displayValue: attempt.sent,
          columnWidth: '15%',
        },
        failed: {
          tableHeader: 'Failed',
          key: 'failed',
          type: 'number',
          value: attempt.totalPhones - attempt.sent,
          displayValue: <Button
            disabled={attempt.totalPhones - attempt.sent === 0}
            onClick={() => handleDownloadFailedFile(attempt.fileName + " - " + attempt.processedDate)}
            styleType={StyleType.TERTIARY}>
            {attempt.totalPhones - attempt.sent} - Download
          </Button>,
          columnWidth: '15%',
        }
      };
    }) || [];
  }, [processResponses]);

  const handleDownload = () => {
    FileHelpers.downloadFile('/templates/genovista-patients-template.csv', 'genovista-patients-template.csv');
  };

  const handleDownloadFailedFile = async (filename: string) => {
    const response = await carecoApi?.getSmsFailedFile(filename).catch(() => {
      addAlert({
        type: ToastType.ERROR,
        content: 'Failed to download failed records file.',
      });
    });
    if (response) {
      const arrayBuffer = FileHelpers.base64ToArrayBuffer(response);
      const blob = new Blob([arrayBuffer], {type: 'text/csv'});
      FileHelpers.downloadFile(URL.createObjectURL(blob), filename + ".csv");
    }
  };

  return (
    <>
      <DownloadWrapper>
        <span>
          <SmallTypography>Download CSV Template </SmallTypography>
          <InfoIconTooltip
            text="This is an example file with the format which is expected for Genovista SMS processing."/>
        </span>
        <Button styleType={StyleType.TERTIARY} onClick={handleDownload}>Download Template</Button>
      </DownloadWrapper>
      <br/>

      <SortableTable
        id="send-sms-table"
        loading={isLoading || isFetching}
        loadError={error}
        data={tableData}
        header={['File name', 'Processed Date & Time', 'Total records', 'Total phones', 'Success', 'Failed']}
        sortableColumns={[]}
        searchColumns={['fileName']}
        searchPlaceholder="Search file name"
      >
        {inProgress && (
          <>
            <Spinner iconSize="2x"/>
            <SmallTypography color={colors.charcoal}>
              Send SMS - this may take some time. The process will continue if you leave this page.
            </SmallTypography>
          </>
        )}
        <Button disabled={isLoading || inProgress || isPending} onClick={() => setIsModalOpen(true)}>
          <Icon iconClass={ICONS.plus} style={{marginRight: sizes.xSmall}}/>
          Upload New File
        </Button>
      </SortableTable>

      <Modal
        headerText={"Send file - SMS"}
        className={css({width: "80rem"})}
        hideModal={() => setIsModalOpen(false)}
        isOpen={isModalOpen}
        buttons={[<Button key="send" disabled={isPending || !isValid() || !!inProgress} onClick={handleSubmit}>
          Send
        </Button>]}
      >
        <FileInput
          acceptedFiles=".csv"
          label="Select a file"
          onChange={(files: File[]) => handleChange(null, 'file', files)}
          value={form.file?.value ?? []}
          buttonText="Browse"
          buttonType={StyleType.SECONDARY}
          error={form.file?.valid.message}
        />
        <br/>
      </Modal>
    </>
  );
};

export default SendSmsTool;
